import React, { useEffect, useMemo, useRef } from "react";
import styles from "./AIImageColorizer.module.css";
import { Button, Container, Typography, Box, Grid } from "@mui/material";
import Navbar from "../../Navbar";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Footer from "../../Footer";
import FAQ from "./FAQ";
import PricingCards from "../../PricingCards";
import ImageSlider from "../../ImageSlider";
import ImageSlider2 from "../../ImageSlider2";
import DownloadSection from "../../DownloadSection";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import image1 from "../../Images/8K Resolution Support.png";
import image2 from "../../Images/Color scratch.png";
import image3 from "../../Images/Historical Color Accuracy.png";
import image4 from "../../Images/One-Click Photo Colorization.png";
import posterImage from "../../Images/ai colorizer.webp";

gsap.registerPlugin(ScrollTrigger);

const AIImageColorizer = () => {
  // SEO Meta Content - Updated for AI Image Colorizer
  const seoData = useMemo(
    () => ({
      title:
        "AI Image Colorizer | Colorize Black & White Photos Automatically | [Your Brand]",
      description:
        "Bring old photos to life with AI-powered colorization. Restore black & white images in seconds with realistic colors. Perfect for historical photos, portraits, and vintage media. Try free online!",
      keywords:
        "AI image colorizer, colorize black and white photos, photo restoration AI, old photo colorization, automatic image colorizer, historical photo restoration, vintage photo colorizer",
      canonical: "https://yourdomain.com/ai-image-colorizer",
      schemaJSON: {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "AI Image Colorizer Pro",
        operatingSystem: "Web, iOS, Android",
        applicationCategory: "PhotoEditor",
        image:
          "https://cdn.yourdomain.com/images/ai-colorizer-demo-comparison.jpg",
        description:
          "Advanced AI-powered tool for restoring and colorizing black & white photos with historical accuracy and realistic tones",
        brand: {
          "@type": "Brand",
          name: "[Your Brand]",
          logo: "https://cdn.yourdomain.com/logo.png",
        },
        offers: {
          "@type": "Offer",
          url: "https://yourdomain.com/ai-image-colorizer",
          priceCurrency: "USD",
          price: "0",
          description:
            "Free for 10 images/month, Pro plans start at $12/mo for unlimited HD colorization",
        },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          ratingCount: "3200",
          bestRating: "5",
          worstRating: "1",
        },
        featureList: [
          "One-click photo colorization",
          "Historical color accuracy",
          "4K resolution support",
          "Batch processing for multiple photos",
          "AI-powered scratch & damage repair",
          "Custom color adjustment tools",
          "Automatic face and skin tone enhancement",
          "Support for JPEG, PNG, and TIFF formats",
          "Real-time preview of colorization results",
          "AI-based noise reduction and sharpening",
        ],
        screenshot: {
          "@type": "ImageObject",
          url: "https://cdn.yourdomain.com/images/colorizer-interface.jpg",
          caption: "AI Image Colorizer dashboard with before/after comparison",
        },
        additionalProperty: [
          {
            "@type": "PropertyValue",
            name: "Supported Formats",
            value: "JPEG, PNG, TIFF, BMP",
          },
          {
            "@type": "PropertyValue",
            name: "Output Resolution",
            value: "Up to 4K (3840x2160)",
          },
          {
            "@type": "PropertyValue",
            name: "Processing Speed",
            value: "<5 seconds per image (1080p)",
          },
          {
            "@type": "PropertyValue",
            name: "Batch Processing",
            value: "Up to 50 images simultaneously",
          },
        ],
        operatingSystem: ["Windows", "macOS", "iOS", "Android"],
        processorRequirements: "Minimum 2GHz CPU, 4GB RAM",
        memoryRequirements: "1GB free disk space",
        softwareRequirements: "WebGL 2.0 support for browser version",
        applicationSubCategory: "Photo Restoration Software",
        downloadUrl: "https://yourdomain.com/download",
        softwareVersion: "3.2.0",
        releaseNotes:
          "Added batch processing and improved historical color accuracy",
        fileSize: "120MB",
        publisher: {
          "@type": "Organization",
          name: "[Your Brand]",
          foundingDate: "2018",
          contactPoint: {
            "@type": "ContactPoint",
            email: "support@yourdomain.com",
            contactType: "customer support",
          },
        },
        reviews: [
          {
            "@type": "Review",
            author: "Jane Doe",
            datePublished: "2023-10-01",
            reviewBody:
              "Incredible tool! Brought my grandparents' wedding photos to life with stunning accuracy.",
            reviewRating: {
              "@type": "Rating",
              ratingValue: "5",
              bestRating: "5",
            },
          },
          {
            "@type": "Review",
            author: "John Smith",
            datePublished: "2023-09-25",
            reviewBody:
              "The batch processing feature saved me hours of work. Highly recommend the Pro version!",
            reviewRating: {
              "@type": "Rating",
              ratingValue: "4.5",
              bestRating: "5",
            },
          },
        ],
      },
    }),
    []
  ); // Empty dependency array ensures this is only calculated once
  // Refs declarations
  const sectionRefs = useRef([]);
  const gradientRef = useRef(null);
  const headingRef = useRef([]);
  const imageRef = useRef(null);
  const buttonRef = useRef(null);
  const featureRefs = useRef([]);
  const videoRefs = useRef([]);
  const sectionHeadingRef = useRef(null);
  const heroContentRef = useRef(null);
  const descriptionRef = useRef(null); // New ref for description
  const navigate = useNavigate();

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Text animations
      gsap.fromTo(
        headingRef.current,
        {
          opacity: 0,
          y: 50,
        },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          stagger: 0.3,
          ease: "power4.out",
          scrollTrigger: {
            trigger: heroContentRef.current,
            start: "top 80%",
            end: "top 50%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Gradient text specific animation
      gsap.fromTo(
        headingRef.current[0],
        {
          backgroundPosition: "100% 100%",
        },
        {
          backgroundPosition: "0% 100%",
          duration: 2,
          ease: "power2.inOut",
          scrollTrigger: {
            trigger: headingRef.current[0],
            start: "top 80%",
            end: "top 30%",
            scrub: 1,
          },
        }
      );

      // Stroke text animation
      gsap.fromTo(
        headingRef.current[1],
        {
          textStrokeWidth: "0px",
          opacity: 0,
        },
        {
          textStrokeWidth: "3px",
          opacity: 1,
          duration: 1.5,
          ease: "power4.out",
          scrollTrigger: {
            trigger: headingRef.current[1],
            start: "top 85%",
            end: "top 60%",
            scrub: 1,
          },
        }
      );

      // Description animation
      gsap.fromTo(
        descriptionRef.current,
        {
          opacity: 0,
          y: 30,
        },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: descriptionRef.current,
            start: "top 90%",
            end: "top 70%",
            toggleActions: "play none none reverse",
          },
        }
      );
      // Gradient position animation
      gsap.fromTo(
        gradientRef.current,
        { backgroundPosition: "0% 0%" },
        {
          backgroundPosition: "100% 100%",
          ease: "power2.inOut",
          scrollTrigger: {
            start: "top top",
            end: "bottom bottom",
            scrub: 1.5,
          },
        }
      );

      // Color intensity animation
      gsap.fromTo(
        gradientRef.current,
        {
          backgroundImage:
            "linear-gradient(315deg, #0a0c24 0%, #1a1b3a 50%,rgb(23, 5, 24) 100%)",
        },
        {
          backgroundImage:
            "linear-gradient(315deg, #050814 0%, #0f1028 50%, #1a081b 100%)",
          ease: "power2.inOut",
          scrollTrigger: {
            start: "top center",
            end: "bottom center",
            scrub: 1,
          },
        }
      );
      // Hero section animations
      gsap.from(imageRef.current, {
        scale: 1.2,
        duration: 1.5,
        ease: "power4.out",
        transformOrigin: "center center",
      });

      const heroTl = gsap.timeline({
        scrollTrigger: {
          trigger: heroContentRef.current,
          start: "top top",
          end: "+=100%",
          scrub: 1.5,
          pin: true,
          anticipatePin: 1,
        },
      });

      heroTl
        .to(imageRef.current, {
          scale: 1.8,
          y: 200,
          opacity: 0,
          ease: "power3.inOut",
        })
        .to(
          heroContentRef.current,
          {
            y: -200,
            opacity: 0,
            ease: "power3.inOut",
          },
          0
        );

      // Feature section animations
      // Section heading animation
      gsap.fromTo(
        sectionHeadingRef.current,
        { y: 50, opacity: 0, filter: "brightness(1)" },
        {
          y: 0,
          opacity: 1,
          filter: "brightness(1.5)",
          duration: 1.5,
          scrollTrigger: {
            trigger: sectionHeadingRef.current,
            start: "top 90%",
            end: "top 60%",
            scrub: 1.5,
            toggleActions: "play none none reverse",
          },
        }
      );

      // Feature sections animations
      features.forEach((feature, index) => {
        const video = videoRefs.current[index];
        const content = featureRefs.current[index];
        const videoContainer = video.parentElement;
        const videoMask = videoContainer.querySelector(".videoMask");
        const direction = feature.direction === "left" ? -1 : 1;

        // Create timeline for coordinated animations
        const videoTl = gsap.timeline({
          scrollTrigger: {
            trigger: videoContainer,
            start: "top bottom-=10%",
            end: "top center",
            scrub: 1.5,
            toggleActions: "play pause resume reset",
          },
        });

        // Video entrance animation
        videoTl
          .fromTo(
            video,
            { scale: 0.9, opacity: 0 },
            { scale: 1, opacity: 1, duration: 1.2 }
          )
          .fromTo(videoMask, { opacity: 1 }, { opacity: 0, duration: 1.5 }, 0);

        // Content animation
        gsap.fromTo(
          content,
          { x: 150 * direction, opacity: 0 },
          {
            x: 0,
            opacity: 1,
            scrollTrigger: {
              trigger: content,
              start: "top 85%",
              end: "top 50%",
              scrub: 1.2,
            },
          }
        );

        // Reverse mask animation on scroll up
        gsap.fromTo(
          videoMask,
          { opacity: 0 },
          {
            opacity: 1,
            scrollTrigger: {
              trigger: videoContainer,
              start: "center center",
              end: "bottom top",
              scrub: 1,
              toggleActions: "play pause resume reset",
            },
          }
        );

        // Content slide animation
        gsap.fromTo(
          content,
          { x: 150 * direction, opacity: 0 },
          {
            x: 0,
            opacity: 1,
            scrollTrigger: {
              trigger: content,
              start: "top 85%",
              end: "top 50%",
              scrub: 1.2,
              toggleActions: "play none none reverse",
            },
          }
        );

        // Reverse animations on scroll up
        gsap.fromTo(
          video,
          { scale: 1 },
          {
            scale: 0.9,
            opacity: 0,
            scrollTrigger: {
              trigger: video,
              start: "top center",
              end: "bottom top",
              scrub: 1,
              toggleActions: "restart pause resume reset",
            },
          }
        );
      });
    });
    return () => ctx.revert();
  }, []);

  // Updated features array for AI Image Colorizer
  const features = [
    {
      title: "One-Click Photo Colorization",
      content:
        "Transform black & white photos into vibrant, realistic images with a single click. Perfect for restoring old family photos and historical archives.",
      video:
        "https://cdn.web.imagine.art/imagine-frontend/assets/video/text-to-video.webm",
      img: image4,
      direction: "left",
    },
    {
      title: "Historical Color Accuracy",
      content:
        "Our AI uses historical references to apply accurate colors to old photos. From vintage clothing to classic cars, every detail is colorized with precision.",
      video:
        "https://cdn.web.imagine.art/imagine-frontend/assets/video/text-to-video.webm",
      img: image3,
      direction: "right",
    },
    {
      title: "4K/8K Resolution Support",
      content:
        "Colorize and upscale photos to 4K or 8K resolution for professional printing or digital display. Ideal for photographers and archivists.",
      video:
        "https://cdn.web.imagine.art/imagine-frontend/assets/video/text-to-video.webm",
      img: image1,
      direction: "left",
    },
    {
      title: "AI-Powered Scratch Repair",
      content:
        "Remove scratches, dust, and damage from old photos while colorizing them. Restore your memories to their original glory.",
      video:
        "https://cdn.web.imagine.art/imagine-frontend/assets/video/text-to-video.webm",
      img: image2,
      direction: "right",
    },
  ];

  return (
    <Box className={styles.mainContainer} ref={gradientRef}>
      {/* Added SEO Head Section */}
      <Helmet>
        <html lang="en" />
        <meta charSet="utf-8" />
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <link rel="canonical" href={seoData.canonical} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={seoData.canonical} />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta
          property="og:image"
          content="https://cdn.photofix.com/og-image.jpg"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta
          name="twitter:image"
          content="https://cdn.photofix.com/twitter-card.jpg"
        />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(seoData.schemaJSON)}
        </script>

        {/* Additional SEO Enhancements */}
        <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
        <meta name="robots" content="index, follow" />
        <link rel="alternate" hreflang="x-default" href={seoData.canonical} />
      </Helmet>
      <Navbar />
      <Container className={styles.contentContainer}>
        <Box className={styles.textContainer}>
          <Typography variant="h1" className={styles.heading}>
            <span
              ref={(el) => (headingRef.current[0] = el)}
              className={styles.gradientText}
            >
              AI Image Colorizer:
            </span>
            <br />
            <span
              ref={(el) => (headingRef.current[1] = el)}
              className={styles.strokeText}
            >
              Bring Old Photos to Life
            </span>
          </Typography>

          <Typography
            variant="subtitle1"
            className={styles.description}
            ref={descriptionRef} // Added ref here
          >
            Restore and colorize black & white photos with AI-powered precision.
            Perfect for historical archives, family albums, and creative
            projects. Try it free today!
          </Typography>

          <Box className={styles.buttonContainer}>
            <Button
              variant="contained"
              className={styles.generateButton}
              ref={buttonRef}
              onClick={() => navigate("/dashboard/ai-image-colorizer")}
            >
              <span className={styles.buttonText}>Colorize Photos Now</span>
              <div className={styles.buttonGlow} />
            </Button>
          </Box>
        </Box>

        <Box className={styles.imageContainer}>
          <div className={styles.imageWrapper}>
            <img
              ref={imageRef}
              src={posterImage}
              alt="AI Art Generator Platform Creating Digital Paintings"
              title="AI Art Generator Platform Creating Digital Paintings"
              class={styles.image}
              width="1200"
              height="630"
              loading="lazy"
              decoding="async"
              aria-describedby="image-caption"
            />
            <div className={styles.imageGlow} />
            <div className={styles.imageShine} />
          </div>
        </Box>
      </Container>
      <ImageSlider />
      <ImageSlider2 />
      <Container maxWidth="xl" className={styles.featureContainer}>
        <div ref={sectionHeadingRef}>
          <h2 className={styles.sectionHeading}>
            Why Choose Our AI Image Colorizer?
          </h2>
          <p className={styles.sectionSubheading}>
            Professional tools for photographers, archivists, and creators
          </p>
        </div>

        {features.map((feature, index) => (
          <section
            key={feature.title}
            ref={(el) => (sectionRefs.current[index] = el)}
            className={styles.featureSection}
          >
            <Grid container spacing={6} alignItems="center">
              <Grid
                item
                xs={12}
                md={6}
                order={{ xs: 2, md: index % 2 ? 2 : 1 }}
              >
                <div
                  ref={(el) => (featureRefs.current[index] = el)}
                  className={`${styles.contentWrapper} feature-content`}
                >
                  <Typography variant="h2" className={styles.featureTitle}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" className={styles.featureText}>
                    {feature.content}
                  </Typography>
                </div>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                order={{ xs: 1, md: index % 2 ? 1 : 2 }}
              >
                <div className={styles.videoContainer}>
                  {/* <video
                    ref={(el) => (videoRefs.current[index] = el)}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className={styles.featureVideo}
                  >
                    <source src={feature.video} type="video/webm" />
                  </video> */}
                  <img
                    className={styles.featureVideo}
                    src={feature.img}
                    ref={(el) => (videoRefs.current[index] = el)}
                    alt="AI Art Generator Platform Creating Digital Paintings"
                    title="AI Art Generator Platform Creating Digital Paintings"
                    width="auto"
                    height="auto"
                    loading="lazy"
                    decoding="async"
                    aria-describedby="image-caption"
                  />
                  <div className={styles.videoMask} />
                </div>
              </Grid>
            </Grid>
          </section>
        ))}
      </Container>
      <PricingCards />
      <DownloadSection />
      <FAQ />
      <Footer />
    </Box>
  );
};

export default AIImageColorizer;
